import React, { useImperativeHandle, useRef } from 'react';
import {
  Animated,
  Dimensions,
  Platform,
  StyleSheet,
  TouchableOpacity,
  TouchableWithoutFeedback,
  View,
} from 'react-native';

import { ViewPropTypes, TextPropTypes } from 'deprecated-react-native-prop-types';
import { isFunction as _isFunction, isObject as _isObject, isString as _isString } from 'lodash';
import memoize from 'memoize-one';
import PropTypes from 'prop-types';

import { Icon } from '../Icon';
import { getThemeImages, Images, LightThemeColors } from '../Themes';
import { withTheme } from '../Themes/Provider';
import { VFHeaderTabs } from '../VFHeaderTabs';
import VFHeaderTabsScrollable from '../VFHeaderTabs/VFHeaderTabsScrollable';
import VFText from '../VFText';

import SubTitle from './Components/SubTitle';
import styles from './VFHeader.Styles';

const setIconName = icon => {
  let iconName = '';

  if (_isString(icon)) {
    iconName = Images[icon];
  } else if (_isObject(icon)) {
    iconName = icon;
  }

  return iconName;
};

const renderIcon = (icon, accessibilityLabel) => {
  const iconName = setIconName(icon);

  return <Icon name={iconName} type="image" size={25.2} accessibilityLabel={accessibilityLabel} />;
};

const getSubHeaderDescriptionStyle = (
  subHeaderDescription,
  subHeaderDescriptionContainerStyle,
  renderDescriptionInfo,
) => {
  const styleArr = [styles.subHeaderDescription];

  if (!subHeaderDescription && !_isFunction(renderDescriptionInfo)) {
    styleArr.push({ justifyContent: 'flex-end' });
  }

  styleArr.push(subHeaderDescriptionContainerStyle);

  return StyleSheet.flatten(styleArr);
};

const getSubHeaderDescriptionStyleMemoize = memoize(getSubHeaderDescriptionStyle);

const onLayout = (event, onHeaderHeightChange) => {
  const { height } = event.nativeEvent.layout;
  _isFunction(onHeaderHeightChange) && onHeaderHeightChange(height);
};
const VFHeader = React.forwardRef(
  (
    {
      title,
      titlePlaceholders,
      titleStyle,
      headerStyle,
      showBack,
      showClose,
      onBack,
      onShare,
      onClose,
      withSubHeader,
      subHeaderDescription,
      subHeaderDescriptionPlaceholders,
      onSubHeaderDescriptionPress,
      subHeaderDescriptionStyle,
      subHeaderDescriptionContainerStyle,
      renderDescriptionInfo,
      leftIcon,
      rightIcon,
      shareIcon,
      backButtonAccessibilityLabel,
      shareButtonAccessibilityLabel,
      closeButtonAccessibilityLabel,
      titleTextAccessibilityLabel,
      subHeaderDescriptionAccessibilityLabel,
      hideMainHeader,
      configureHeaderTabs,
      customHeaderContent,
      theme,
      allowFontScaling,
      subHeaderFullContainerStyle,
      subHeaderMaxHeight = 110,
      closeButtonTestID,
      backButtonTestID,
      shareButtonTestID,
      onHeaderHeightChange,
      headerContainerViewStyle,
      leftIconContainerStyle,
      rightIconContainerStyle,
      shareIconContainerStyle,
      tabContainerStyle,
      ...subTitleProps
    },
    ref,
  ) => {
    const subHeaderDescriptionViewStyle = getSubHeaderDescriptionStyleMemoize(
      subHeaderDescription,
      subHeaderDescriptionContainerStyle,
      renderDescriptionInfo,
    );

    const scrollOffset = useRef(new Animated.Value(0));

    const SUB_HEADER_MAX_HEIGHT = subHeaderMaxHeight;
    const SUB_HEADER_MIN_HEIGHT = 0;

    const subHeaderHeightInterpolated = scrollOffset.current.interpolate({
      inputRange: [0, SUB_HEADER_MAX_HEIGHT],
      outputRange: [SUB_HEADER_MAX_HEIGHT, SUB_HEADER_MIN_HEIGHT],
      extrapolate: 'clamp',
    });

    // prevent the negative value of scrolling
    const clampedScrollY = scrollOffset.current.interpolate({
      inputRange: [0, 1],
      outputRange: [0, 1],
      extrapolateLeft: 'clamp',
    });

    const subHeaderTranslateInterpolated = clampedScrollY.interpolate({
      inputRange: [0, SUB_HEADER_MAX_HEIGHT],
      outputRange: [0, SUB_HEADER_MAX_HEIGHT - SUB_HEADER_MIN_HEIGHT],
      extrapolateLeft: 'clamp',
    });

    const getNegativeValueOfAnimation = animatedValue => {
      return Animated.multiply(animatedValue, -1);
    };

    useImperativeHandle(ref, () => ({
      handleAnimationOnScroll: Animated.event(
        [{ nativeEvent: { contentOffset: { y: scrollOffset.current } } }],
        { useNativeDriver: false },
      ),
    }));

    const images = getThemeImages(theme.name);
    return (
      <View
        onLayout={event => onLayout(event, onHeaderHeightChange)}
        style={[styles.containerStyle(theme, configureHeaderTabs), headerStyle,]}
      >
        {!hideMainHeader && (
          <View style={[styles.headerContainerStyle, headerContainerViewStyle, { paddingHorizontal: Platform.OS === 'web' && Dimensions.get('window').width > 700 ? '10%' : 24 }]}>
            <View style={[styles.iconContainerStyle, leftIconContainerStyle]}>
              {showBack && (
                <TouchableWithoutFeedback style={{ cursor: 'pointer' }} onPress={onBack} testID={backButtonTestID}>
                  {renderIcon(leftIcon || images.ic_arrow_left, backButtonAccessibilityLabel)}
                </TouchableWithoutFeedback>
              )}
            </View>

            {!!title && (
              <View style={styles.headerStyle}>
                <VFText
                  i18nKey={title}
                  placeHolders={titlePlaceholders}
                  style={[styles.headerTextStyle(theme), titleStyle]}
                  testID={titleTextAccessibilityLabel}
                  allowFontScaling={allowFontScaling}
                />
              </View>
            )}

            {_isFunction(onShare) && (
              <TouchableWithoutFeedback onPress={onShare} testID={shareButtonTestID}>
                <View style={[styles.shareButtonContainer, shareIconContainerStyle]}>
                  {renderIcon(shareIcon || images.ic_share, shareButtonAccessibilityLabel)}
                </View>
              </TouchableWithoutFeedback>
            )}
            <View style={[styles.iconContainerStyle, rightIconContainerStyle]}>
              {showClose && (
                <TouchableWithoutFeedback
                  onPress={onClose}
                  testID={closeButtonTestID}
                  hitSlop={{
                    bottom: 15,
                    left: 15,
                    right: 15,
                    top: 15,
                  }}
                >
                  {renderIcon(rightIcon || images.ic_close, closeButtonAccessibilityLabel)}
                </TouchableWithoutFeedback>
              )}
            </View>
          </View>
        )}
        {customHeaderContent}
        {!customHeaderContent && withSubHeader && (
          <Animated.View
            style={[
              styles.subHeaderContainerStyle(configureHeaderTabs, subHeaderHeightInterpolated),
              subHeaderFullContainerStyle,
              { paddingHorizontal: Platform.OS === 'web' && Dimensions.get('window').width > 700 ? '10%' : 24 }
            ]}
          >
            <Animated.View
              style={{
                transform: [
                  {
                    translateY: getNegativeValueOfAnimation(subHeaderTranslateInterpolated),
                  },
                ],
              }}
            >
              <SubTitle {...subTitleProps} allowFontScaling={allowFontScaling} />

              {renderDescriptionInfo || subHeaderDescription ? (
                <View style={subHeaderDescriptionViewStyle}>
                  {!!subHeaderDescription && (
                    <TouchableOpacity
                      onPress={onSubHeaderDescriptionPress}
                      disabled={!_isFunction(onSubHeaderDescriptionPress)}
                      style={styles.subHeaderDescriptionTouchableOpacity}
                    >
                      <VFText
                        i18nKey={subHeaderDescription}
                        style={[
                          styles.subHeaderDescriptionMainText(theme),
                          subHeaderDescriptionStyle,
                        ]}
                        placeHolders={subHeaderDescriptionPlaceholders}
                        testID={subHeaderDescriptionAccessibilityLabel}
                        allowFontScaling={allowFontScaling}
                        numberOfLines={2}
                      />
                    </TouchableOpacity>
                  )}
                  {renderDescriptionInfo && renderDescriptionInfo()}
                </View>
              ) : null}
            </Animated.View>
          </Animated.View>
        )}

        {configureHeaderTabs &&
          (configureHeaderTabs.withTabsScroll ? (
            <VFHeaderTabsScrollable {...configureHeaderTabs} allowFontScaling={allowFontScaling} />
          ) : (
            <VFHeaderTabs
              {...configureHeaderTabs}
              allowFontScaling={allowFontScaling}
              containerStyle={[
                withSubHeader && configureHeaderTabs?.tabs && styles.tabContainerStyle,
                tabContainerStyle,
              ]}
            />
          ))}
      </View>
    );
  },
);

VFHeader.defaultProps = {
  showClose: true,
  showBack: false,
  withSubHeader: false,
  titleStyle: {},
  headerStyle: {},
  subHeaderTitleStyle: {},
  subHeaderTitleContainerStyle: {},
  subHeaderDescriptionStyle: {},
  subHeaderDescriptionContainerStyle: {},
  subHeaderBeforeSubTitleStyle: {},
  subHeaderAfterSubTitleStyle: {},
  subHeaderSubTitleStyle: {},
  subHeaderMaxHeight: Platform.select({ android: 98, ios: 102 }),
  theme: LightThemeColors,
  renderSubHeaderRightComponent: null,
  onSubHeaderDescriptionPress: null,
  subHeaderTitleNumberOfLines: undefined,
};

VFHeader.propTypes = {
  titleStyle: TextPropTypes.style,
  headerStyle: ViewPropTypes.style,
  showClose: PropTypes.bool,
  showBack: PropTypes.bool,
  leftIcon: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  rightIcon: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  shareIcon: PropTypes.object,
  title: PropTypes.string.isRequired,
  subHeaderTitleNumberOfLines: PropTypes.number,
  titlePlaceholders: PropTypes.arrayOf(PropTypes.string),
  subHeaderDescriptionPlaceholders: PropTypes.arrayOf(PropTypes.string),
  onBack: PropTypes.func,
  onShare: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  withSubHeader: PropTypes.bool,
  subHeaderTitle: PropTypes.string,
  subHeaderSubTitlePlaceholders: PropTypes.arrayOf(PropTypes.any),
  subHeaderTitlePlaceholders: PropTypes.arrayOf(PropTypes.any),
  subHeaderSubTitle: PropTypes.string,
  renderSubHeaderRightComponent: PropTypes.func,
  subTitleSubHeaderAndRightComponentContainerStyle: ViewPropTypes.style,
  subHeaderRightComponentStyle: ViewPropTypes.style,
  subHeaderBeforeSubTitle: PropTypes.string,
  subHeaderAfterSubTitle: PropTypes.string,
  subHeaderDescription: PropTypes.string,
  renderDescriptionInfo: PropTypes.func,
  renderSubHeaderSubTitle: PropTypes.func,
  renderSubHeaderTitle: PropTypes.func,
  subHeaderTitleStyle: TextPropTypes.style,
  subHeaderTitleContainerStyle: ViewPropTypes.style,
  subHeaderDescriptionStyle: ViewPropTypes.style,
  subHeaderDescriptionContainerStyle: ViewPropTypes.style,
  subHeaderBeforeSubTitleStyle: ViewPropTypes.style,
  subHeaderAfterSubTitleStyle: ViewPropTypes.style,
  subHeaderFullContainerStyle: ViewPropTypes.style,
  subHeaderSubTitleStyle: TextPropTypes.style,
  backButtonAccessibilityLabel: ViewPropTypes.accessibilityLabel,
  shareButtonAccessibilityLabel: ViewPropTypes.accessibilityLabel,
  closeButtonAccessibilityLabel: ViewPropTypes.accessibilityLabel,
  titleTextAccessibilityLabel: ViewPropTypes.accessibilityLabel,
  subHeaderTitleAccessibilityLabel: ViewPropTypes.accessibilityLabel,
  subHeaderSubTitleAccessibilityLabel: ViewPropTypes.accessibilityLabel,
  subHeaderBeforeSubTitleAccessibilityLabel: ViewPropTypes.accessibilityLabel,
  subHeaderAfterSubTitleAccessibilityLabel: ViewPropTypes.accessibilityLabel,
  subHeaderDescriptionAccessibilityLabel: ViewPropTypes.accessibilityLabel,
  subHeaderMaxHeight: PropTypes.number,
  hideMainHeader: PropTypes.bool,
  configureHeaderTabs: PropTypes.shape({
    withTabsScroll: PropTypes.bool,
    tabs: PropTypes.array,
  }),
  theme: PropTypes.shape({
    name: PropTypes.string,
    isDark: PropTypes.bool,
    colors: PropTypes.object,
  }),
  onHeaderHeightChange: PropTypes.func,
  customHeaderContent: PropTypes.node,
  allowFontScaling: PropTypes.bool,
  closeButtonTestID: ViewPropTypes.testID,
  backButtonTestID: ViewPropTypes.testID,
  shareButtonTestID: ViewPropTypes.testID,
  headerContainerViewStyle: ViewPropTypes.style,
  subHeaderSubTitleViewStyle: ViewPropTypes.style,
  onSubHeaderDescriptionPress: PropTypes.func,
  leftIconContainerStyle: ViewPropTypes.style,
  shareIconContainerStyle: ViewPropTypes.style,
  rightIconContainerStyle: ViewPropTypes.style,
  tabContainerStyle: ViewPropTypes.style,
};

export default withTheme(VFHeader);
